import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCampaignPlan } from 'modules/api/campaignPlan';
import { cancelFunctions } from 'utils/cancelFunctions';
import { SelectedLine } from 'components/common/AllocationReport/AllocationReport.types';
import { CampaignPlanDetails, CampaignPlanError } from 'components/common/types/CampaignPlan.types';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeCampaignPlanDetails,
  changeCampaignPlanError,
  clearSharedCampaignPlan,
  setIsLoading,
} from 'store/sharedCampaignPlan/reducer';
import {
  getCampaignPlanDetails,
  getCampaignPlanError,
  getIsLoading,
  getAllocationReportDealLines,
  getAllocationReportSelectAllDefault,
  getAllocationReportSelectAllLocked,
  getAllocationReportSelectHidden,
  getLocaleCode,
} from 'store/sharedCampaignPlan/selectors';

export interface UsePublicCampaignPlan {
  allocationReportDealLines: SelectedLine[];
  isAllocationReportSelectAllDefault: boolean;
  isAllocationReportSelectAllLocked: boolean;
  isSelectHidden: boolean;
  campaignPlanDetails: CampaignPlanDetails | null;
  campaignPlanError: CampaignPlanError | null;
  isLoading: boolean;
  localeCode: string;
  currencyCode: string;
  token?: string;
}

export const usePublicCampaignPlan = (): UsePublicCampaignPlan => {
  const dispatch = useDispatch();
  const { token } = useParams();

  const isLoading = useSelector(getIsLoading);
  const campaignPlanDetails = useSelector(getCampaignPlanDetails);
  const campaignPlanError = useSelector(getCampaignPlanError);
  const allocationReportDealLines = useSelector(getAllocationReportDealLines);
  const isAllocationReportSelectAllDefault = useSelector(getAllocationReportSelectAllDefault);
  const isAllocationReportSelectAllLocked = useSelector(getAllocationReportSelectAllLocked);
  const isSelectHidden = useSelector(getAllocationReportSelectHidden);
  const localeCode = useSelector(getLocaleCode); // TODO: get locale code from the campaign plan - to be added by BE in DS-3879
  const currencyCode = 'EUR'; // TODO: get currency code from the campaign plan - to be added by BE in DS-3879

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!token) return;

      try {
        dispatch(setIsLoading(true));

        const response = await getCampaignPlan(token, cancelFunctions);

        dispatch(changeCampaignPlanDetails(response));
      } catch (error) {
        dispatch(changeCampaignPlanError(error as CampaignPlanError));
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchData();

    return () => {
      dispatch(clearSharedCampaignPlan());
    };
  }, [token]);

  return {
    allocationReportDealLines,
    isAllocationReportSelectAllDefault,
    isAllocationReportSelectAllLocked,
    isSelectHidden,
    campaignPlanDetails,
    campaignPlanError,
    isLoading,
    localeCode,
    currencyCode,
    token,
  };
};
